import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button, Divider, InputAdornment } from '@material-ui/core';
import Formsy from 'formsy-react';
import { bindActionCreators } from 'redux';
import * as Actions from '../../store/actions';
import { SelectSearchFormsy, TextFieldFormsy } from '@fuse';
import { formatUserErrorMessage, isSuperUser } from '../../../utils';
import { Alert } from '@mui/material';
import PermissionSelector from '../../custom-widgets/PermissionsSelector/PermissionSelector';
import { applyLinkSelection, ExistingIdentityLinkSection, LinkGuardedButton, getClassLinkProps, linkStateDefaults, LINK_MODES, runLinkCreate } from './components/ExistingIdentityLinkControl';
import { roleCategories } from '../form-components/ReactHookProducerFormSelects';

class AdminForm extends Component {
    state = {
        canSubmit: false,
        alert: null,
        ...linkStateDefaults,
        permissions: new Set([]),
        permissionsDirty: false,
    };

    handlePermissionChange = (newPermissions) => {
        this.setState({ permissions: newPermissions });
        this.setState({ permissionsDirty: true });
    };

    handleRoleCategoryChange = (role_category) => {
        this.setState({ role_category });
    };

    disableButton = () => {
        this.setState({ canSubmit: false });
    };

    enableButton = () => {
        this.setState({ canSubmit: true });
    };

    navigateToListUsers = () => {
        const { history } = this.props;
        history.replace({ pathname: '/list-admins' });
    };

    onSubmit = (model) => {
        const { editData, editUser, showMessage, addAdmin } = { ...this.state, ...this.props };
        const submitModel = { ...model };
        if (this.state.permissionsDirty) submitModel.permissions = Array.from(this.state.permissions);

        if (editData) {
            delete submitModel.username;
            delete submitModel.email;
            editUser(submitModel, editData._id)
                .then(() => {
                    showMessage({ message: 'Admin edited successfully.' });
                    this.setState({ alert: null });
                    this.navigateToListUsers();
                })
                .catch((err) => {
                    showMessage({ message: formatUserErrorMessage(err, 'Admin', 'edit') });
                });
        } else {
            const link = getClassLinkProps(this);
            if (link.submitGuard()) {
                // PFM-3029: confirm the identity in the link control before
                // submitting (the control renders its guidance — never silent).
                return;
            }
            this.runCreate(applyLinkSelection(submitModel, link.linkSelection));
        }
    };

    runCreate = (createModel) => {
        const { showMessage, addAdmin } = this.props;
        const link = getClassLinkProps(this);
        runLinkCreate({
            action: addAdmin,
            model: createModel,
            onSuccess: () => {
                showMessage({ message: 'Admin added successfully.' });
                this.setState({ alert: null });
                this.navigateToListUsers();
            },
            onError: (err) => {
                showMessage({ message: formatUserErrorMessage(err, 'Admin', 'add') });
                if (err.message) {
                    this.setState({ alert: err.message });
                }
            },
            onLinkOffer: (linkPrompt) => this.setState({ linkPrompt }),
            onResetLink: (msg) => showMessage({ message: link.resetLink(msg) }),
        });
    };

    render() {
        const { canSubmit } = this.state;
        const { editData } = this.props;

        return (
            <div className="min-w-3/4 max-w-3/4">
                <Divider />

                <Formsy
                    onValidSubmit={this.onSubmit}
                    onValid={this.enableButton}
                    onInvalid={this.disableButton}
                    /* eslint-disable-next-line no-return-assign */
                    ref={(form) => (this.form = form)}
                    className="flex flex-col justify-center"
                >
                    <ExistingIdentityLinkSection targetRole="admin" isSubuser={false} show={!editData} link={getClassLinkProps(this)} onConfirmLink={() => getClassLinkProps(this).confirmLinkPrompt(this.runCreate)} />

                    {this.state.alert && (
                        <Alert className="mt-32 mb-16" color="error">
                            {this.state.alert}
                        </Alert>
                    )}

                    <TextFieldFormsy
                        className="my-16"
                        type="text"
                        name="name"
                        label="Name"
                        value={editData ? editData.name : ''}
                        validations={{ minLength: 2 }}
                        validationErrors={{ minLength: 'Min character length is 2' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <i className="text-20 material-icons" color="action">
                                        person
                                    </i>
                                </InputAdornment>
                            ),
                        }}
                        required
                        variant="outlined"
                    />

                    {this.state.linkMode !== LINK_MODES.LINK && (
                        <TextFieldFormsy
                            className="my-16"
                            type="text"
                            name="username"
                            label="Username"
                            value={editData ? editData.username : ''}
                            validations={{ ...(!editData && { minLength: 4 }) }}
                            validationErrors={{ minLength: 'Min character length is 4' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <i className="text-20 material-icons" color="action">
                                            bookmark
                                        </i>
                                    </InputAdornment>
                                ),
                            }}
                            required={!(this.state.linkMode === LINK_MODES.LINK)}
                            variant="outlined"
                            disabled={!!editData || this.state.linkMode === LINK_MODES.LINK}
                        />
                    )}

                    {!editData && this.state.linkMode === LINK_MODES.NEW && (
                        <TextFieldFormsy
                            className="my-16"
                            type="password"
                            name="password"
                            label="Password"
                            validations={{ minLength: 8, equalsField: 'password_confirmation' }}
                            validationErrors={{ equalsField: 'Passwords do not match', minLength: 'Password must be at least 8 characters' }}
                            InputProps={{
                                autoComplete: 'new-password',
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <i className="text-20 material-icons" color="action">
                                            vpn_key
                                        </i>
                                    </InputAdornment>
                                ),
                            }}
                            variant="outlined"
                            required
                        />
                    )}

                    {!editData && this.state.linkMode === LINK_MODES.NEW && (
                        <TextFieldFormsy
                            className="my-16"
                            type="password"
                            name="password_confirmation"
                            label="Confirm Password"
                            validations={{ minLength: 8, equalsField: 'password' }}
                            validationErrors={{ equalsField: 'Passwords do not match', minLength: 'Password must be at least 8 characters' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <i className="text-20 material-icons" color="action">
                                            vpn_key
                                        </i>
                                    </InputAdornment>
                                ),
                            }}
                            variant="outlined"
                            required
                        />
                    )}

                    {this.state.linkMode !== LINK_MODES.LINK && (
                        <TextFieldFormsy
                            className="my-16"
                            type="text"
                            name="email"
                            label="Email"
                            validations="isEmail"
                            value={editData ? editData.email : ''}
                            validationError="This is not a valid email"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <i className="text-20 material-icons" color="action">
                                            alternate_email
                                        </i>
                                    </InputAdornment>
                                ),
                            }}
                            required={!(this.state.linkMode === LINK_MODES.LINK)}
                            variant="outlined"
                            disabled={!!editData || this.state.linkMode === LINK_MODES.LINK}
                        />
                    )}

                    <TextFieldFormsy
                        className="my-16"
                        type="text"
                        name="secondary_email"
                        label="Secondary Email"
                        validations="isEmail"
                        value={editData ? editData.secondary_email : ''}
                        validationError="This is not a valid email"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <i className="text-20 material-icons" color="action">
                                        alternate_email
                                    </i>
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                    />

                    <SelectSearchFormsy
                        className="my-16"
                        name="role_category"
                        label="Role Category *"
                        options={roleCategories}
                        value={editData ? editData.role_category : ''}
                        onChange={(value) => this.handleRoleCategoryChange(value)}
                        variant="outlined"
                        required
                        // wrap
                    />

                    {isSuperUser() && <PermissionSelector permissionType={'Admin'} handlePermissionChange={this.handlePermissionChange} selectedValues={!!editData && !this.state.permissionsDirty ? new Set(editData.permissions) : this.state.permissions} />}

                    <LinkGuardedButton blocked={getClassLinkProps(this).submitGuard()} type="submit" variant="contained" color="primary" className="mx-auto my-16" aria-label={editData ? 'EDIT ADMIN' : 'ADD ADMIN'} disabled={!canSubmit}>
                        {editData ? 'Edit Admin' : 'Add Admin'}
                    </LinkGuardedButton>
                </Formsy>
            </div>
        );
    }
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            addAdmin: Actions.addAdmin,
            editUser: Actions.editUser,
            showMessage: Actions.showMessage,
        },
        dispatch
    );
}

export default withRouter(connect(null, mapDispatchToProps)(AdminForm));
